import { createFileRoute } from "@tanstack/react-router";
import { Subscription } from "@/lib/lazyPages";
import {
  SEO,
  subscriptionFaqJsonLd,
  commerceJsonLd,
  buildLearningJourneysCourseJsonLd,
  learningJourneysFaqJsonLd,
} from "@/config/seoConfig";
import { seoHead } from "@/lib/seo/head";
import { publicSubscriptionPlansQuery } from "@/lib/subscriptionPlansQuery";
import type { PublicSubscriptionPlan } from "@/lib/subscriptionPlans.functions";

import { getSchemaPrices } from "@/lib/seo/pricing.functions";
import { INTRO_OFFERS_ENABLED } from "@/lib/introPassPath";


export const Route = createFileRoute("/subscription/")({
  // Plans are public catalogue data, so prefetching them here puts the real
  // plan cards in the SSR HTML instead of a skeleton (removes the LCP swap).
  // The same values feed the Offer prices, so schema can never disagree with
  // the admin-configured amounts.
  loader: async ({ context }) => {
    // Fails soft: this page is prerendered at build time, and a plan read that
    // cannot complete must not take the whole static page down — the client
    // refetches the same query on hydration.
    let plans: PublicSubscriptionPlan[] | null = null;


    try {
      plans = await context.queryClient.ensureQueryData(publicSubscriptionPlansQuery);
    } catch {
      plans = null;
    }

    let prices = { trialPrice: null as number | null };
    try {
      prices = await getSchemaPrices();
    } catch {
      // Offers are omitted rather than guessed when the read fails.
    }
    return {
      plans: (plans ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        price: Number(p.price ?? 0),
        discounted_price: p.discounted_price ?? null,
        duration_days: p.duration_days ?? null,
        is_intro_pass: p.is_intro_pass ?? null,
      })),
      ...prices,
    };
  },
  head: ({ loaderData }) => {
    // Lowest live membership price feeds the Course Offer, so schema can never
    // disagree with the admin-configured amounts.
    // Intro offers are paused: the pass plan and its price stay out of schema.
    const plans = (loaderData?.plans ?? []).filter(
      (p) => INTRO_OFFERS_ENABLED || !p.is_intro_pass,
    );
    const membershipPrices = plans
      .map((p) => Number(p.discounted_price ?? p.price ?? 0))
      .filter((n) => Number.isFinite(n) && n > 0);
    // One FAQPage per page: the courses questions (previously on the gated
    // /learning-journeys page) are merged into this page's FAQ node rather than
    // emitted as a second, competing FAQPage.
    const faq = {
      ...subscriptionFaqJsonLd,
      mainEntity: [
        ...(subscriptionFaqJsonLd.mainEntity ?? []),
        ...(learningJourneysFaqJsonLd.mainEntity ?? []),
      ],
    };
    return seoHead({
      ...SEO.subscription,
      jsonLd: [
        faq,
        buildLearningJourneysCourseJsonLd({
          membershipPrice: membershipPrices.length ? Math.min(...membershipPrices) : null,
        }),
        ...commerceJsonLd({
          trialPrice: INTRO_OFFERS_ENABLED ? (loaderData?.trialPrice ?? undefined) : undefined,
          plans,
        }),
      ],
    });
  },

  component: Subscription,
});
